import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, CircularProgress, IconButton,
  Typography, Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { initiateRecharge, verifyRecharge, clearWalletError } from '../../store/slices/walletSlice';
import { openRazorpayCheckout, generateIdempotencyKey } from '../../utils/razorpay';
import { openCashfreeCheckout } from '../../utils/cashfree';
import RechargePackages from './RechargePackages';

const MIN = 5;
const MAX = 100000;

const RechargeModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((s) => s.wallet);
  const user = useSelector((s) => s.auth?.user);

  const [amount, setAmount] = useState('');
  const [localError, setLocalError] = useState('');
  const [isSandbox, setIsSandbox] = useState(false);

  const handleSelect = (preset) => {
    setAmount(String(preset));
    setLocalError('');
  };

  const handleChange = (e) => {
    setAmount(e.target.value);
    setLocalError('');
    dispatch(clearWalletError('recharge'));
  };

  const handleClose = () => {
    setAmount('');
    setLocalError('');
    setIsSandbox(false);
    dispatch(clearWalletError('recharge'));
    onHide();
  };

  const handlePay = async () => {
    const num = parseFloat(amount);
    if (!num || num < MIN) {
      setLocalError(`Minimum recharge is ₹${MIN}`);
      return;
    }
    if (num > MAX) {
      setLocalError(`Maximum recharge is ₹${MAX}`);
      return;
    }

    const idempotencyKey = generateIdempotencyKey('recharge');

    try {
      const result = await dispatch(
        initiateRecharge({ amount: num, currency: 'INR', idempotencyKey })
      ).unwrap();

      const gateway = result.gateway || 'razorpay';
      const sandbox = result.key_id?.startsWith('rzp_test_') || result.key_id?.startsWith('TEST');

      if (sandbox) setIsSandbox(true);

      if (gateway === 'cashfree') {
        const cfSandbox = result.key_id?.startsWith('TEST') ?? true;
        openCashfreeCheckout({
          paymentSessionID: result.payment_session_id,
          orderID: result.order_id,
          sandbox: cfSandbox,
          onSuccess: async () => {
            const verifyKey = generateIdempotencyKey('verify');
            await dispatch(
              verifyRecharge({
                paymentData: { order_id: result.order_id },
                idempotencyKey: verifyKey,
              })
            ).unwrap();
            handleClose();
          },
          onFailure: (err) => {
            setLocalError(err?.message || 'Payment failed. Please try again.');
          },
        });
      } else {
        await openRazorpayCheckout({
          keyId: result.key_id,
          orderId: result.order_id,
          amount: result.amount,
          currency: result.currency || 'INR',
          userEmail: user?.email || '',
          userName: user?.name || '',
          onSuccess: async (paymentResponse) => {
            const verifyKey = generateIdempotencyKey('verify');
            await dispatch(
              verifyRecharge({
                paymentData: {
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_order_id: paymentResponse.razorpay_order_id,
                  razorpay_signature: paymentResponse.razorpay_signature,
                },
                idempotencyKey: verifyKey,
              })
            ).unwrap();
            handleClose();
          },
          onDismiss: () => {
            setLocalError('Payment cancelled. You can try again.');
          },
        });
      }
    } catch (err) {
      // errors handled via Redux error state
    }
  };

  const displayError = localError || error.recharge;

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#6c5ce7',
          color: '#fff',
          py: 1.5,
          px: 2.5,
        }}
      >
        <AccountBalanceWalletIcon fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          Add Money to Wallet
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 1, px: 2.5 }}>
        <RechargePackages selected={parseFloat(amount) || null} onSelect={handleSelect} />

        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Or enter custom amount (₹)
        </Typography>
        <TextField
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: MIN, max: MAX, step: 1 }}
          placeholder={`Min ₹${MIN}`}
          value={amount}
          onChange={handleChange}
          sx={{ mb: 1.5 }}
        />

        {isSandbox && (
          <Alert severity="info" sx={{ mb: 1, py: 0.5, fontSize: 12 }}>
            <strong>Sandbox mode</strong> — Test UPI:{' '}
            <code>success@cashfree</code> (success) |{' '}
            <code>failure@cashfree</code> (failure)
          </Alert>
        )}

        {displayError && (
          <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: 13 }}>
            {displayError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClose}
          sx={{ color: '#666', borderColor: '#ccc' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={loading.recharge || !amount}
          onClick={handlePay}
          sx={{
            bgcolor: '#6c5ce7',
            '&:hover': { bgcolor: '#5a4bd1' },
            minWidth: 100,
          }}
        >
          {loading.recharge ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            `Pay ₹${amount || '0'}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RechargeModal;
